import React, { memo } from 'react';
import styled from 'styled-components';

import { ImageBanner } from 'shared/components/image-banner';
import { mediaQueries } from 'shared/styles';
import useDispensary from 'src/dispensary/hooks/use-dispensary';
import useUI from 'src/hooks/use-ui';
import { ImageBannerPlatformType } from 'src/gql/graphql';
import type { ImageBanner as ImageBannerData } from 'src/gql/graphql';
import { useActiveScheduledItems } from 'src/dispensary/hooks/use-active-scheduled-items';
import MenuImage from './menu-image';

export type MenuImageBannerProps = {
  banners: ImageBannerData[] | null;
};

export function getEnabledBanners(banners: ImageBannerData[], isKiosk: boolean): ImageBannerData[] {
  return banners.reduce<ImageBannerData[]>((acc, banner) => {
    const platformType = isKiosk ? ImageBannerPlatformType.kiosk : ImageBannerPlatformType.web;
    const platform = banner.platforms?.find((p) => p?.type === platformType);

    if (!(platform?.enabled ?? true)) {
      return acc;
    }

    return [
      ...acc,
      {
        ...banner,
        link: platform?.link ?? banner.link,
        openInNewTab: platform?.openInNewTab ?? banner.openInNewTab,
      },
    ];
  }, []);
}

export const TEST_ID_MENU_IMAGE_BANNER = 'menu-image-banner';
export const TEST_ID_SCROLL_ITEM_CONTAINER = 'scroll-item-container';

const MenuImageBannerComponent = ({ banners }: MenuImageBannerProps): JSX.Element => {
  const { dispensary } = useDispensary();
  const { isKiosk } = useUI();
  const activeBanners = useActiveScheduledItems({
    items: banners ?? [],
    getSchedule: (banner) => banner.schedule,
    dispensaryTimezone: dispensary?.timezone,
    enablePolling: true,
  });

  const enabledBanners = getEnabledBanners(activeBanners, isKiosk);

  if (!enabledBanners.length) {
    return <></>;
  }

  const carouselOptions = {
    draggable: enabledBanners.length > 1,
    loop: true,
    startIndex: 0,
    slidesToScroll: 1,
    banners: enabledBanners,
  };

  return (
    <ImageBanner carouselOptions={carouselOptions}>
      <MainImageScrollContainer data-testid={TEST_ID_MENU_IMAGE_BANNER}>
        {enabledBanners.map((banner, index) => (
          <ScrollItem
            key={banner._id}
            data-testid={TEST_ID_SCROLL_ITEM_CONTAINER}
            isSingleBanner={enabledBanners.length === 1}
          >
            <MenuImage banner={banner} index={index} />
          </ScrollItem>
        ))}
      </MainImageScrollContainer>
    </ImageBanner>
  );
};

export const MenuImageBanner = memo(MenuImageBannerComponent);
export default MenuImageBanner;

const MainImageScrollContainer = styled.div`
  display: flex;
`;

const ScrollItem = styled.div<{ isSingleBanner?: boolean }>`
  min-width: 100%;
  display: flex;
  justify-content: center;

  @media ${mediaQueries.largePhone} {
    margin-right: ${({ isSingleBanner }) => (isSingleBanner ? '0' : '-44px')};
  }
`;
